/**
 * Tracking-Seite für Endkunden (öffentlich und als Live-Vorschau im Composer). Eigene Texte je Sprache (de/en nach
 * Lieferland bzw. Umschalter) – bewusst nicht über t(), denn die Sprache hier ist die des Endkunden, nicht des Portals.
 */
import { blockVisible, estimateDelivery, ltextOf, textOn, type LText, type TrackingBlock, type TrackingOrderView, type TrackingPageConfig, type TrackingPageLang } from "@logiful/domain";
import { AlertTriangle, ArrowRight, Box, Check, CheckCircle2, ChevronDown, ClipboardList, Copy, ExternalLink, Home, Mail, MapPin, Music2, Package, PackageCheck, Search, Store, Truck, Undo2, XCircle } from "lucide-react";
import { useEffect, useState, type CSSProperties, type ReactNode } from "react";

// ── Texte (Endkunden-Sprache) ──
const L = {
  de: {
    phase: { ordered: "Bestellt", processing: "In Bearbeitung", shipped: "Versendet", in_transit: "Unterwegs", out_for_delivery: "In Zustellung", pickup_ready: "Zur Abholung bereit", delivered: "Zugestellt", problem: "Zustellproblem", returned: "Zurück an den Absender", cancelled: "Storniert" },
    phaseText: {
      ordered: "Wir haben deine Bestellung erhalten und bereiten alles vor.",
      processing: "Deine Bestellung wird gerade im Lager gepackt.",
      shipped: "Dein Paket ist verpackt und wurde an den Versanddienstleister übergeben.",
      in_transit: "Dein Paket ist auf dem Weg zu dir.",
      out_for_delivery: "Heute ist es so weit – dein Paket ist in der Zustellung.",
      pickup_ready: "Dein Paket liegt zur Abholung für dich bereit.",
      delivered: "Dein Paket wurde zugestellt. Viel Freude damit!",
      problem: "Bei der Zustellung gab es ein Problem. Details siehst du im Verlauf.",
      returned: "Das Paket ist auf dem Rückweg zu uns. Wir melden uns bei dir.",
      cancelled: "Diese Bestellung wurde storniert.",
    },
    steps: ["Bestellt", "In Bearbeitung", "Versendet", "Unterwegs", "In Zustellung", "Zugestellt"],
    order: "Bestellung",
    eta: "Voraussichtliche Zustellung",
    today: "Heute",
    deliveredOn: "Zugestellt am",
    history: "Sendungsverlauf",
    showAll: "Alle Ereignisse anzeigen",
    showLess: "Weniger anzeigen",
    noEvents: "Sobald der Versanddienstleister die ersten Daten meldet, erscheinen sie hier.",
    items: "Paketinhalt",
    to: "Lieferung nach",
    parcel: "Paket",
    of: "von",
    carrier: "Beim Versanddienstleister ansehen",
    copy: "Code kopieren",
    copied: "Kopiert!",
    searchTitle: "Wo ist meine Bestellung?",
    searchText: "Gib deine Bestellnummer und die PLZ der Lieferadresse oder deine E-Mail-Adresse ein.",
    orderNumber: "Bestellnummer",
    contact: "PLZ oder E-Mail",
    find: "Sendung finden",
    notFound: "Wir haben keine passende Bestellung gefunden. Bitte prüfe deine Angaben.",
    tooMany: "Zu viele Versuche – bitte warte kurz und versuche es erneut.",
    invalidLink: "Dieser Link ist ungültig oder abgelaufen.",
    imprint: "Impressum",
    privacy: "Datenschutz",
    follow: "Folge uns",
  },
  en: {
    phase: { ordered: "Ordered", processing: "Processing", shipped: "Shipped", in_transit: "In transit", out_for_delivery: "Out for delivery", pickup_ready: "Ready for pickup", delivered: "Delivered", problem: "Delivery issue", returned: "Returned to sender", cancelled: "Cancelled" },
    phaseText: {
      ordered: "We've received your order and are getting everything ready.",
      processing: "Your order is being packed in our warehouse.",
      shipped: "Your parcel is packed and has been handed over to the carrier.",
      in_transit: "Your parcel is on its way to you.",
      out_for_delivery: "Today's the day – your parcel is out for delivery.",
      pickup_ready: "Your parcel is ready for pickup.",
      delivered: "Your parcel has been delivered. Enjoy!",
      problem: "There was a problem with the delivery. See the tracking history for details.",
      returned: "The parcel is on its way back to us. We'll be in touch.",
      cancelled: "This order has been cancelled.",
    },
    steps: ["Ordered", "Processing", "Shipped", "In transit", "Out for delivery", "Delivered"],
    order: "Order",
    eta: "Estimated delivery",
    today: "Today",
    deliveredOn: "Delivered on",
    history: "Tracking history",
    showAll: "Show all events",
    showLess: "Show less",
    noEvents: "As soon as the carrier reports the first scan, it will appear here.",
    items: "In this parcel",
    to: "Delivering to",
    parcel: "Parcel",
    of: "of",
    carrier: "View on carrier website",
    copy: "Copy code",
    copied: "Copied!",
    searchTitle: "Where is my order?",
    searchText: "Enter your order number and the postcode of the delivery address or your email address.",
    orderNumber: "Order number",
    contact: "Postcode or email",
    find: "Track order",
    notFound: "We couldn't find a matching order. Please check your details.",
    tooMany: "Too many attempts – please wait a moment and try again.",
    invalidLink: "This link is invalid or has expired.",
    imprint: "Imprint",
    privacy: "Privacy",
    follow: "Follow us",
  },
};
export type TrackingTexts = (typeof L)["de"];
export const trackingTexts = (lang: TrackingPageLang): TrackingTexts => L[lang];

const FONTS: Record<string, { body: string; head: string; headWeight: number }> = {
  modern: { body: `"Inter Variable", ui-sans-serif, system-ui, sans-serif`, head: `"Inter Variable", ui-sans-serif, system-ui, sans-serif`, headWeight: 700 },
  geometric: { body: `"Inter Variable", ui-sans-serif, system-ui, sans-serif`, head: `"Montserrat", "Inter Variable", ui-sans-serif, sans-serif`, headWeight: 700 },
  elegant: { body: `"Inter Variable", ui-sans-serif, system-ui, sans-serif`, head: `"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, serif`, headWeight: 600 },
  rounded: { body: `ui-rounded, "SF Pro Rounded", "Nunito", "Inter Variable", system-ui, sans-serif`, head: `ui-rounded, "SF Pro Rounded", "Nunito", "Inter Variable", system-ui, sans-serif`, headWeight: 700 },
};
const RADIUS = { sharp: 6, soft: 14, round: 24 } as const;

/** Farben je Hintergrund – Karten heben sich immer ab, Text immer lesbar */
function palette(cfg: TrackingPageConfig) {
  const { brand, accent, background } = cfg.theme;
  const dark = background === "dark";
  return {
    dark,
    brand,
    brandFg: textOn(brand),
    accent,
    accentFg: textOn(accent),
    page:
      background === "dark" ? "#0b0b0e"
      : background === "light" ? "#ffffff"
      : background === "brand" ? "#f6f5f3"
      : `radial-gradient(900px 420px at 15% -120px, ${accent}24, transparent 70%), radial-gradient(900px 420px at 90% -80px, ${brand}14, transparent 70%), #faf9f7`,
    card: dark ? "#16161b" : "#ffffff",
    cardBorder: dark ? "#26262e" : "rgba(17,17,17,0.07)",
    fg: dark ? "#f4f4f5" : "#141414",
    muted: dark ? "#a1a1aa" : "#5f5f66",
    subtle: dark ? "#71717a" : "#8e8e96",
    track: dark ? "#2a2a33" : "#ecebe8",
    shadow: dark ? "none" : "0 1px 2px rgba(16,16,16,0.04), 0 8px 24px -12px rgba(16,16,16,0.12)",
  };
}
type Pal = ReturnType<typeof palette>;

const dayFmt = (ymd: string, lang: TrackingPageLang) => new Date(`${ymd}T12:00:00Z`).toLocaleDateString(lang === "de" ? "de-DE" : "en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/Berlin" });
const dateTime = (iso: string, lang: TrackingPageLang) => new Date(iso).toLocaleString(lang === "de" ? "de-DE" : "en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" });
const ymdBerlin = (d: Date) => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin" }).format(d);

export interface TrackingViewProps {
  config: TrackingPageConfig;
  shop: string;
  lang: TrackingPageLang;
  onLang?: (l: TrackingPageLang) => void;
  /** Bild-URL zu einer Asset-ID */
  asset: (id: string) => string;
  /** null = Suchseite */
  order: TrackingOrderView | null;
  /** Paket vorwählen (?p=) */
  preselect?: string | null;
  search?: { enabled: boolean; onSubmit: (orderNumber: string, contact: string) => Promise<"ok" | "not_found" | "rate_limited">; error?: string | null };
  /** Composer: Klick auf einen Baustein markiert ihn */
  selectedBlock?: string | null;
  onSelectBlock?: (id: string) => void;
  /** Composer-Vorschau: kompakte Breite erzwingen */
  narrow?: boolean;
}

export function TrackingView(p: TrackingViewProps) {
  const { config, lang } = p;
  const pal = palette(config);
  const font = FONTS[config.theme.font] ?? FONTS.modern!;
  const r = RADIUS[config.theme.radius];
  const tx = L[lang];
  const style = {
    background: pal.page,
    color: pal.fg,
    fontFamily: font.body,
    "--tp-r": `${r}px`,
    "--tp-card": pal.card,
    "--tp-border": pal.cardBorder,
    "--tp-muted": pal.muted,
    "--tp-subtle": pal.subtle,
    "--tp-brand": pal.brand,
    "--tp-brand-fg": pal.brandFg,
    "--tp-accent": pal.accent,
    "--tp-accent-fg": pal.accentFg,
    "--tp-track": pal.track,
    "--tp-shadow": pal.shadow,
    "--tp-head": font.head,
    "--tp-head-weight": String(font.headWeight),
  } as CSSProperties;

  const blocks = p.order ? config.blocks.filter((b) => blockVisible(b.showWhen, p.order!.phase)) : config.blocks.filter((b) => b.type !== "status" && b.type !== "eta" && b.type !== "items" && !b.showWhen.length);
  const band = config.theme.background === "brand";
  const first = blocks[0];
  const lead = band && first?.type === "hero" && !first.image ? first : null;
  const rest = lead ? blocks.slice(1) : blocks;
  const wrap = `mx-auto w-full px-4 ${p.narrow ? "max-w-[640px]" : "max-w-[680px]"}`;

  return (
    <div className={`tp-root w-full antialiased ${p.narrow ? "min-h-full" : "min-h-dvh"}`} style={style}>
      <style>{TP_CSS}</style>
      <div className="@container">
        {band ? (
          // Hintergrund „Markenfarbe“: Kopf (und ein Titel ohne Bild) im Farbband, Karten überlappen es
          <div style={{ background: pal.brand, color: pal.brandFg }} className="pb-16">
            <div className={wrap}>
              <Header config={config} shop={p.shop} lang={lang} onLang={p.onLang} asset={p.asset} pal={pal} />
              {lead && (
                <Selectable id={lead.id} selected={p.selectedBlock === lead.id} onSelect={p.onSelectBlock}>
                  <Block block={lead} {...p} pal={pal} inBand />
                </Selectable>
              )}
            </div>
          </div>
        ) : (
          <div className={wrap}>
            <Header config={config} shop={p.shop} lang={lang} onLang={p.onLang} asset={p.asset} pal={pal} />
          </div>
        )}
        <div className={`${wrap} ${p.narrow ? "pb-10" : "pb-16"} ${band ? "relative -mt-10" : ""}`}>
          {!p.order && <SearchCard tx={tx} search={p.search} />}
          <div className="flex flex-col gap-4">
            {rest.map((b) => (
              <Selectable key={b.id} id={b.id} selected={p.selectedBlock === b.id} onSelect={p.onSelectBlock}>
                <Block block={b} {...p} pal={pal} />
              </Selectable>
            ))}
          </div>
          <Footer config={config} lang={lang} pal={pal} />
        </div>
      </div>
    </div>
  );
}

function Selectable({ id, selected, onSelect, children }: { id: string; selected: boolean; onSelect?: ((id: string) => void) | undefined; children: ReactNode }) {
  if (!onSelect) return <>{children}</>;
  return (
    <div
      onClickCapture={(e) => {
        e.preventDefault();
        onSelect(id);
      }}
      className={`relative cursor-pointer rounded-[calc(var(--tp-r)+6px)] outline-offset-4 transition-[outline-color] ${selected ? "outline-2 outline-[#f97361]" : "outline-2 outline-transparent hover:outline-[#f9736155]"}`}
      style={{ outlineStyle: "solid" }}
    >
      {children}
    </div>
  );
}

function Header({ config, shop, lang, onLang, asset, pal }: { config: TrackingPageConfig; shop: string; lang: TrackingPageLang; onLang?: ((l: TrackingPageLang) => void) | undefined; asset: (id: string) => string; pal: Pal }) {
  const logo = config.theme.logo;
  const inner = logo ? <img src={asset(logo)} alt={shop} className="h-9 max-w-[180px] object-contain" /> : <span className="text-[19px] tracking-tight" style={{ fontFamily: "var(--tp-head)", fontWeight: 700 }}>{shop}</span>;
  return (
    <header className="flex items-center justify-between gap-3 py-6">
      {config.theme.shopUrl ? <a href={config.theme.shopUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center">{inner}</a> : inner}
      {onLang && (
        <div className="flex rounded-full p-0.5 text-[12px] font-semibold" style={{ background: pal.dark || (config.theme.background === "brand" && pal.brandFg === "#ffffff") ? "rgba(255,255,255,0.14)" : "rgba(17,17,17,0.07)" }}>
          {(["de", "en"] as const).map((l) => (
            <button key={l} type="button" onClick={() => onLang(l)} className="rounded-full px-2.5 py-1 uppercase transition-colors" style={l === lang ? { background: pal.card, color: pal.fg, boxShadow: "0 1px 2px rgba(0,0,0,.08)" } : { opacity: 0.75 }}>
              {l}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}

const Card = ({ children, className = "", style }: { children: ReactNode; className?: string; style?: CSSProperties }) => (
  <section className={`tp-card overflow-hidden ${className}`} style={style}>
    {children}
  </section>
);
const H = ({ children, className = "" }: { children: ReactNode; className?: string }) => (
  <h2 className={`tp-head text-[17px] leading-tight ${className}`}>{children}</h2>
);

function Block(props: TrackingViewProps & { block: TrackingBlock; pal: Pal; inBand?: boolean }) {
  const { block: b, lang, asset } = props;
  const tl = (x: LText) => ltextOf(x, lang);
  switch (b.type) {
    case "hero":
      return <Hero b={b} lang={lang} asset={asset} inBand={!!props.inBand} />;
    case "status":
      return props.order ? <StatusBlock b={b} order={props.order} lang={lang} preselect={props.preselect ?? null} /> : null;
    case "eta":
      return props.order ? <EtaBlock b={b} order={props.order} lang={lang} /> : null;
    case "items":
      return props.order && props.order.items.length ? <ItemsBlock b={b} order={props.order} lang={lang} /> : null;
    case "promo":
      return <Promo b={b} lang={lang} asset={asset} pal={props.pal} />;
    case "products":
      return b.items.length ? (
        <div>
          {tl(b.headline) && <H className="mb-3 px-1">{tl(b.headline)}</H>}
          <div className="tp-scroll -mx-4 flex snap-x gap-3 overflow-x-auto px-4 pb-1">
            {b.items.map((it) => {
              const card = (
                <div className="tp-card w-[164px] shrink-0 snap-start overflow-hidden">
                  <div className="aspect-square w-full bg-[var(--tp-track)]">{it.image && <img src={asset(it.image)} alt="" className="h-full w-full object-cover" loading="lazy" />}</div>
                  <div className="p-3">
                    <p className="line-clamp-2 text-[13.5px] font-medium leading-snug">{tl(it.title)}</p>
                    {it.price && <p className="mt-1 text-[13px] text-[var(--tp-muted)]">{it.price}</p>}
                  </div>
                </div>
              );
              return it.url ? <a key={it.id} href={it.url} target="_blank" rel="noopener noreferrer" className="transition-transform hover:-translate-y-0.5">{card}</a> : <div key={it.id}>{card}</div>;
            })}
          </div>
        </div>
      ) : null;
    case "text":
      return (
        <Card className={`p-6 ${b.align === "center" ? "text-center" : ""}`}>
          {tl(b.headline) && <H className="mb-2">{tl(b.headline)}</H>}
          <p className="whitespace-pre-line text-[14.5px] leading-relaxed text-[var(--tp-muted)]">{tl(b.body)}</p>
        </Card>
      );
    case "faq":
      return b.items.length ? (
        <Card className="px-6 py-5">
          {tl(b.headline) && <H className="mb-2">{tl(b.headline)}</H>}
          <div className="divide-y divide-[var(--tp-border)]">
            {b.items.map((q) => (
              <details key={q.id} className="tp-faq group py-3.5">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-[14.5px] font-medium">
                  {tl(q.q)}
                  <ChevronDown className="size-4 shrink-0 text-[var(--tp-subtle)] transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-2 whitespace-pre-line text-[14px] leading-relaxed text-[var(--tp-muted)]">{tl(q.a)}</p>
              </details>
            ))}
          </div>
        </Card>
      ) : null;
    case "buttons": {
      // ohne Link nicht anzeigen (sonst ein toter Button beim Endkunden)
      const items = b.items.filter((it) => it.url && ltextOf(it.label, lang));
      return items.length ? (
        <div className={`grid gap-2.5 ${items.length > 1 ? "@xl:grid-cols-2" : ""}`}>
          {items.map((it) => (
            <a key={it.id} href={it.url} target={it.url.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer" className={`tp-btn ${it.style === "primary" ? "tp-btn-primary" : "tp-btn-secondary"}`}>
              {tl(it.label)}
              <ArrowRight className="size-4" />
            </a>
          ))}
        </div>
      ) : null;
    }
    case "social":
      return <Social b={b} lang={lang} />;
    case "newsletter":
      return (
        <Card className="p-6">
          <div className="flex items-start gap-4">
            <span className="grid size-11 shrink-0 place-items-center rounded-full" style={{ background: "var(--tp-accent)", color: "var(--tp-accent-fg)" }}>
              <Mail className="size-5" />
            </span>
            <div className="min-w-0 flex-1">
              <H>{tl(b.title)}</H>
              {tl(b.text) && <p className="mt-1 text-[14px] leading-relaxed text-[var(--tp-muted)]">{tl(b.text)}</p>}
              {b.buttonUrl && tl(b.buttonLabel) && (
                <a href={b.buttonUrl} target="_blank" rel="noopener noreferrer" className="tp-btn tp-btn-primary mt-4 w-auto">
                  {tl(b.buttonLabel)}
                </a>
              )}
            </div>
          </div>
        </Card>
      );
    case "image":
      if (!b.image) return null;
      return b.url ? (
        <a href={b.url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-[var(--tp-r)]">
          <img src={asset(b.image)} alt={tl(b.alt)} className="w-full object-cover transition-transform duration-500 hover:scale-[1.02]" loading="lazy" />
        </a>
      ) : (
        <img src={asset(b.image)} alt={tl(b.alt)} className="w-full rounded-[var(--tp-r)] object-cover" loading="lazy" />
      );
  }
}

function Hero({ b, lang, asset, inBand }: { b: Extract<TrackingBlock, { type: "hero" }>; lang: TrackingPageLang; asset: (id: string) => string; inBand: boolean }) {
  const title = ltextOf(b.title, lang);
  const text = ltextOf(b.text, lang);
  const label = ltextOf(b.buttonLabel, lang);
  if (!b.image)
    return (
      <div className="px-1 pt-2 pb-1">
        {title && <h1 className="tp-head text-[30px] leading-[1.1] tracking-tight @xl:text-[36px]">{title}</h1>}
        {text && <p className={`mt-2 max-w-[52ch] text-[15.5px] leading-relaxed ${inBand ? "opacity-80" : "text-[var(--tp-muted)]"}`}>{text}</p>}
        {b.buttonUrl && label && <a href={b.buttonUrl} target="_blank" rel="noopener noreferrer" className={`tp-btn mt-4 w-auto ${inBand ? "bg-white text-black" : "tp-btn-primary"}`}>{label}<ArrowRight className="size-4" /></a>}
      </div>
    );
  return (
    <div className={`relative overflow-hidden rounded-[var(--tp-r)] ${b.height === "tall" ? "min-h-[340px]" : "min-h-[220px]"} flex items-end`} style={{ boxShadow: "var(--tp-shadow)" }}>
      <img src={asset(b.image)} alt="" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0) 25%, rgba(0,0,0,0.62) 100%)" }} />
      <div className="relative p-6 text-white @xl:p-8">
        {title && <h1 className="tp-head text-[28px] leading-[1.1] tracking-tight drop-shadow-sm @xl:text-[34px]">{title}</h1>}
        {text && <p className="mt-2 max-w-[48ch] text-[15px] leading-relaxed text-white/85">{text}</p>}
        {b.buttonUrl && label && (
          <a href={b.buttonUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-[14px] font-semibold text-black transition-transform hover:-translate-y-0.5">
            {label}
            <ArrowRight className="size-4" />
          </a>
        )}
      </div>
    </div>
  );
}

const PHASE_ICON = { ordered: ClipboardList, processing: Box, shipped: Package, in_transit: Truck, out_for_delivery: Truck, pickup_ready: Store, delivered: PackageCheck, problem: AlertTriangle, returned: Undo2, cancelled: XCircle } as const;
const STEP_ICON = [ClipboardList, Box, Package, Truck, Home, CheckCircle2];

function StatusBlock({ b, order, lang, preselect }: { b: Extract<TrackingBlock, { type: "status" }>; order: TrackingOrderView; lang: TrackingPageLang; preselect: string | null }) {
  const tx = L[lang];
  const [pkgIdx, setPkgIdx] = useState(() => Math.max(0, order.packages.findIndex((x) => x.trackingNumber === preselect)));
  const [all, setAll] = useState(false);
  useEffect(() => setPkgIdx((i) => (i < order.packages.length ? i : 0)), [order.packages.length]);
  const pkg = order.packages[pkgIdx] ?? null;
  const problem = order.phase === "problem" || order.phase === "returned";
  const cancelled = order.phase === "cancelled";
  const Icon = PHASE_ICON[order.phase];
  const headline = ltextOf(b.headline, lang);
  const events = pkg?.events ?? [];
  const shown = all ? events : events.slice(0, 4);
  const done = order.phase === "delivered";
  const pct = Math.min(100, (order.step / 5) * 100);
  const toneBg = problem ? "#fef2f2" : cancelled ? "var(--tp-track)" : done ? "#ecfdf3" : "color-mix(in srgb, var(--tp-brand) 10%, transparent)";
  const toneFg = problem ? "#b42318" : cancelled ? "var(--tp-muted)" : done ? "#067647" : "var(--tp-brand)";

  return (
    <Card>
      <div className="p-6 @xl:p-7">
        {headline && <p className="mb-3 text-[13px] font-medium text-[var(--tp-muted)]">{headline}</p>}
        <div className="flex items-start gap-4">
          <span className={`relative grid size-14 shrink-0 place-items-center rounded-2xl ${order.phase === "out_for_delivery" ? "tp-bounce" : ""}`} style={{ background: toneBg, color: toneFg }}>
            <Icon className="size-7" strokeWidth={1.8} />
            {!done && !problem && !cancelled && <span className="tp-ping absolute -right-0.5 -top-0.5 size-3 rounded-full" style={{ background: "var(--tp-accent)" }} />}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[12.5px] font-medium uppercase tracking-[0.08em] text-[var(--tp-subtle)]">
              {tx.order} {order.orderNumber}
            </p>
            <h1 className="tp-head mt-0.5 text-[26px] leading-tight tracking-tight @xl:text-[30px]" style={problem ? { color: "#b42318" } : undefined}>
              {tx.phase[order.phase]}
            </h1>
            <p className="mt-1 text-[14.5px] leading-relaxed text-[var(--tp-muted)]">{tx.phaseText[order.phase]}</p>
          </div>
        </div>

        {b.style === "steps" && !cancelled && (
          <div className="mt-7">
            <div className="relative mx-[14px]">
              <div className="absolute left-0 right-0 top-[13px] h-[3px] rounded-full bg-[var(--tp-track)]" />
              <div className="tp-progress absolute left-0 top-[13px] h-[3px] rounded-full" style={{ width: `${pct}%`, background: problem ? "#d92d20" : "var(--tp-brand)" }} />
              <ol className="relative flex justify-between">
                {tx.steps.map((s, i) => {
                  const reached = i <= order.step;
                  const current = i === order.step && !done;
                  const StepIcon = STEP_ICON[i]!;
                  return (
                    <li key={s} className="flex w-0 flex-col items-center">
                      <span
                        className={`grid size-[29px] place-items-center rounded-full border-2 transition-colors ${current ? "tp-current" : ""}`}
                        style={reached ? { background: problem && current ? "#d92d20" : "var(--tp-brand)", borderColor: problem && current ? "#d92d20" : "var(--tp-brand)", color: "var(--tp-brand-fg)" } : { background: "var(--tp-card)", borderColor: "var(--tp-track)", color: "var(--tp-subtle)" }}
                      >
                        {reached && !current ? <Check className="size-3.5" strokeWidth={3} /> : <StepIcon className="size-3.5" strokeWidth={2.2} />}
                      </span>
                      <span className={`mt-2 hidden w-[84px] text-center text-[11.5px] leading-tight @lg:block ${reached ? "font-medium" : "text-[var(--tp-subtle)]"}`}>{s}</span>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-[var(--tp-muted)]">
          {(order.destination.city || order.destination.zip) && (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="size-4" /> {tx.to} {order.destination.zip} {order.destination.city}
              {order.destination.country && order.destination.country !== "DE" ? `, ${order.destination.country}` : ""}
            </span>
          )}
          {pkg && (
            <span className="inline-flex items-center gap-1.5">
              <Truck className="size-4" /> {pkg.carrier} · <span className="font-mono text-[12.5px]">{pkg.trackingNumber}</span>
            </span>
          )}
        </div>
      </div>

      {order.packages.length > 1 && (
        <div className="tp-scroll flex gap-1.5 overflow-x-auto border-t border-[var(--tp-border)] px-6 py-3">
          {order.packages.map((x, i) => (
            <button
              key={x.trackingNumber}
              type="button"
              onClick={() => setPkgIdx(i)}
              className="shrink-0 rounded-full px-3 py-1.5 text-[12.5px] font-medium transition-colors"
              style={i === pkgIdx ? { background: "var(--tp-brand)", color: "var(--tp-brand-fg)" } : { background: "var(--tp-track)" }}
            >
              {tx.parcel} {i + 1} {tx.of} {order.packages.length}
            </button>
          ))}
        </div>
      )}

      {b.showEvents && pkg && (
        <div className="border-t border-[var(--tp-border)] px-6 py-5 @xl:px-7">
          <p className="mb-3 text-[13px] font-semibold">{tx.history}</p>
          {events.length ? (
            <ol className="relative">
              {shown.map((e, i) => (
                <li key={`${e.at}-${i}`} className="relative flex gap-4 pb-4 last:pb-0">
                  {i < shown.length - 1 && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-[var(--tp-track)]" />}
                  <span className="relative mt-1.5 size-[11px] shrink-0 rounded-full border-2" style={i === 0 ? { background: "var(--tp-brand)", borderColor: "var(--tp-brand)", boxShadow: "0 0 0 4px color-mix(in srgb, var(--tp-brand) 18%, transparent)" } : { background: "var(--tp-card)", borderColor: "var(--tp-track)" }} />
                  <div className="min-w-0">
                    <p className={`text-[14px] leading-snug ${i === 0 ? "font-medium" : "text-[var(--tp-muted)]"}`}>{e.text}</p>
                    <p className="mt-0.5 text-[12px] text-[var(--tp-subtle)]">
                      {dateTime(e.at, lang)}
                      {e.location ? ` · ${e.location}` : ""}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-[13.5px] text-[var(--tp-muted)]">{tx.noEvents}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            {events.length > 4 ? (
              <button type="button" onClick={() => setAll((v) => !v)} className="text-[13px] font-medium underline-offset-4 hover:underline" style={{ color: "var(--tp-brand)" }}>
                {all ? tx.showLess : tx.showAll}
              </button>
            ) : (
              <span />
            )}
            {pkg.carrierUrl && (
              <a href={pkg.carrierUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[12.5px] text-[var(--tp-subtle)] hover:underline">
                {tx.carrier} <ExternalLink className="size-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </Card>
  );
}

function EtaBlock({ b, order, lang }: { b: Extract<TrackingBlock, { type: "eta" }>; order: TrackingOrderView; lang: TrackingPageLang }) {
  const tx = L[lang];
  const today = ymdBerlin(new Date());
  if (order.phase === "delivered" && order.deliveredAt)
    return (
      <Card className="flex items-center gap-4 p-5">
        <span className="grid size-11 place-items-center rounded-full bg-[#ecfdf3] text-[#067647]">
          <PackageCheck className="size-5" />
        </span>
        <div>
          <p className="text-[12.5px] text-[var(--tp-muted)]">{tx.deliveredOn}</p>
          <p className="tp-head text-[19px]">{dateTime(order.deliveredAt, lang)}</p>
        </div>
      </Card>
    );
  if (!order.eta) return null;
  const same = order.eta.from === order.eta.to;
  const label = same ? (order.eta.from === today ? tx.today : dayFmt(order.eta.from, lang)) : `${order.eta.from === today ? tx.today : dayFmt(order.eta.from, lang)} – ${dayFmt(order.eta.to, lang)}`;
  return (
    <Card className="flex items-center justify-between gap-4 p-5" style={{ background: "var(--tp-brand)", color: "var(--tp-brand-fg)", borderColor: "transparent" }}>
      <div>
        <p className="text-[12.5px] opacity-80">{ltextOf(b.headline, lang) || tx.eta}</p>
        <p className="tp-head mt-0.5 text-[21px] leading-tight tracking-tight @lg:text-[24px]">{label}</p>
      </div>
      <span className="grid size-12 place-items-center rounded-full" style={{ background: "rgba(255,255,255,0.16)" }}>
        <Truck className="size-6" />
      </span>
    </Card>
  );
}

function ItemsBlock({ b, order, lang }: { b: Extract<TrackingBlock, { type: "items" }>; order: TrackingOrderView; lang: TrackingPageLang }) {
  return (
    <Card className="p-6">
      <H className="mb-4">{ltextOf(b.headline, lang) || L[lang].items}</H>
      <ul className="flex flex-col gap-3">
        {order.items.map((it, i) => (
          <li key={`${it.name}-${i}`} className="flex items-center gap-3.5">
            {b.showImages && (
              <span className="relative grid size-14 shrink-0 place-items-center overflow-hidden rounded-[calc(var(--tp-r)*0.6)] bg-[var(--tp-track)]">
                {it.image ? <img src={it.image} alt="" className="h-full w-full object-cover" loading="lazy" referrerPolicy="no-referrer" /> : <Package className="size-5 text-[var(--tp-subtle)]" />}
              </span>
            )}
            <span className="min-w-0 flex-1 text-[14.5px] leading-snug">{it.name}</span>
            <span className="shrink-0 rounded-full bg-[var(--tp-track)] px-2.5 py-0.5 text-[12.5px] font-semibold tabular-nums">× {it.quantity}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Promo({ b, lang, asset, pal }: { b: Extract<TrackingBlock, { type: "promo" }>; lang: TrackingPageLang; asset: (id: string) => string; pal: Pal }) {
  const tx = L[lang];
  const [copied, setCopied] = useState(false);
  const bg = b.background ?? pal.accent;
  const fg = textOn(bg);
  const label = ltextOf(b.buttonLabel, lang);
  return (
    <section className="relative overflow-hidden rounded-[var(--tp-r)]" style={{ background: bg, color: fg, boxShadow: "var(--tp-shadow)" }}>
      <div className="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full" style={{ background: "rgba(255,255,255,0.14)" }} />
      <div className="pointer-events-none absolute -bottom-20 right-20 size-40 rounded-full" style={{ background: "rgba(255,255,255,0.08)" }} />
      <div className={`relative flex flex-col gap-5 p-6 @xl:p-7 ${b.image ? "@xl:flex-row @xl:items-center" : ""}`}>
        {b.image && <img src={asset(b.image)} alt="" className="h-36 w-full rounded-[calc(var(--tp-r)*0.7)] object-cover @xl:h-32 @xl:w-40" />}
        <div className="min-w-0 flex-1">
          <p className="tp-head text-[22px] leading-tight tracking-tight">{ltextOf(b.title, lang)}</p>
          {ltextOf(b.text, lang) && <p className="mt-1.5 text-[14.5px] leading-relaxed" style={{ opacity: 0.85 }}>{ltextOf(b.text, lang)}</p>}
          <div className="mt-4 flex flex-wrap items-center gap-2.5">
            {b.code && (
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard?.writeText(b.code).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1800);
                  });
                }}
                className="inline-flex items-center gap-2 rounded-[calc(var(--tp-r)*0.5)] border-2 border-dashed px-3.5 py-2 font-mono text-[15px] font-semibold tracking-wider"
                style={{ borderColor: fg === "#ffffff" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.35)" }}
                title={tx.copy}
              >
                {b.code}
                <span className="text-[11.5px] font-sans font-medium normal-case tracking-normal" style={{ opacity: 0.8 }}>{copied ? tx.copied : <Copy className="size-3.5" />}</span>
              </button>
            )}
            {b.buttonUrl && label && (
              <a href={b.buttonUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-[14px] font-semibold transition-transform hover:-translate-y-0.5" style={{ background: fg, color: bg }}>
                {label}
                <ArrowRight className="size-4" />
              </a>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const SOCIAL: { key: "instagram" | "tiktok" | "facebook" | "youtube" | "pinterest" | "x" | "linkedin"; label: string; glyph: ReactNode }[] = [
  { key: "instagram", label: "Instagram", glyph: <svg viewBox="0 0 24 24" className="size-[18px]" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" /></svg> },
  { key: "tiktok", label: "TikTok", glyph: <Music2 className="size-[18px]" /> },
  { key: "facebook", label: "Facebook", glyph: <span className="text-[17px] font-bold leading-none">f</span> },
  { key: "youtube", label: "YouTube", glyph: <svg viewBox="0 0 24 24" className="size-[19px]" fill="currentColor"><path d="M4 6.5C4 5.67 4.67 5 5.5 5h13c.83 0 1.5.67 1.5 1.5v11c0 .83-.67 1.5-1.5 1.5h-13C4.67 19 4 18.33 4 17.5v-11Z" opacity=".25" /><path d="M10 9v6l5-3-5-3Z" /></svg> },
  { key: "pinterest", label: "Pinterest", glyph: <span className="text-[16px] font-bold leading-none">P</span> },
  { key: "x", label: "X", glyph: <svg viewBox="0 0 24 24" className="size-[16px]" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><path d="M5 5l14 14M19 5L5 19" /></svg> },
  { key: "linkedin", label: "LinkedIn", glyph: <span className="text-[14px] font-bold leading-none">in</span> },
];

function Social({ b, lang }: { b: Extract<TrackingBlock, { type: "social" }>; lang: TrackingPageLang }) {
  const links = SOCIAL.filter((s) => b.links[s.key]);
  if (!links.length) return null;
  return (
    <div className="flex flex-col items-center gap-3 py-2">
      <p className="text-[13px] font-medium text-[var(--tp-muted)]">{ltextOf(b.headline, lang) || L[lang].follow}</p>
      <div className="flex flex-wrap justify-center gap-2.5">
        {links.map((s) => (
          <a key={s.key} href={b.links[s.key]} target="_blank" rel="noopener noreferrer" aria-label={s.label} className="tp-card grid size-11 place-items-center rounded-full transition-transform hover:-translate-y-0.5" style={{ borderRadius: 999 }}>
            {s.glyph}
          </a>
        ))}
      </div>
    </div>
  );
}

function SearchCard({ tx, search }: { tx: TrackingTexts; search?: TrackingViewProps["search"] | undefined }) {
  const [n, setN] = useState("");
  const [c, setC] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  if (!search?.enabled)
    return (
      <Card className="mb-4 p-6">
        <p className="text-[14.5px] text-[var(--tp-muted)]">{search?.error ?? tx.invalidLink}</p>
      </Card>
    );
  return (
    <Card className="mb-4 p-6 @xl:p-7">
      <span className="mb-4 grid size-12 place-items-center rounded-2xl" style={{ background: "color-mix(in srgb, var(--tp-brand) 10%, transparent)", color: "var(--tp-brand)" }}>
        <Search className="size-6" />
      </span>
      <h1 className="tp-head text-[26px] leading-tight tracking-tight">{tx.searchTitle}</h1>
      <p className="mt-1.5 text-[14.5px] leading-relaxed text-[var(--tp-muted)]">{tx.searchText}</p>
      <form
        className="mt-5 grid gap-3 @xl:grid-cols-[1fr_1fr_auto]"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!n.trim() || c.trim().length < 3) return;
          setBusy(true);
          setErr(null);
          const r = await search.onSubmit(n.trim(), c.trim()).catch(() => "not_found" as const);
          setBusy(false);
          if (r === "not_found") setErr(tx.notFound);
          if (r === "rate_limited") setErr(tx.tooMany);
        }}
      >
        <input className="tp-input" placeholder={tx.orderNumber} aria-label={tx.orderNumber} value={n} onChange={(e) => setN(e.target.value)} autoComplete="off" />
        <input className="tp-input" placeholder={tx.contact} aria-label={tx.contact} value={c} onChange={(e) => setC(e.target.value)} autoComplete="postal-code" />
        <button type="submit" disabled={busy} className="tp-btn tp-btn-primary disabled:opacity-60">
          {tx.find}
        </button>
      </form>
      {(err ?? search.error) && <p className="mt-3 text-[13.5px] text-[#b42318]">{err ?? search.error}</p>}
    </Card>
  );
}

function Footer({ config, lang, pal }: { config: TrackingPageConfig; lang: TrackingPageLang; pal: Pal }) {
  const tx = L[lang];
  const text = ltextOf(config.theme.footer, lang);
  if (!text && !config.theme.imprintUrl && !config.theme.privacyUrl) return null;
  return (
    <footer className="mt-10 flex flex-col items-center gap-2 text-center text-[12.5px]" style={{ color: pal.subtle }}>
      {text && <p className="whitespace-pre-line">{text}</p>}
      <div className="flex gap-4">
        {config.theme.imprintUrl && <a href={config.theme.imprintUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{tx.imprint}</a>}
        {config.theme.privacyUrl && <a href={config.theme.privacyUrl} target="_blank" rel="noopener noreferrer" className="hover:underline">{tx.privacy}</a>}
      </div>
    </footer>
  );
}

/** Beispieldaten für die Vorschau im Composer */
export function sampleTrackingOrder(phase: TrackingOrderView["phase"], lang: TrackingPageLang): TrackingOrderView {
  const now = Date.now();
  const iso = (h: number) => new Date(now - h * 3_600_000).toISOString();
  const step = { ordered: 0, processing: 1, shipped: 2, in_transit: 3, problem: 3, returned: 3, out_for_delivery: 4, pickup_ready: 4, delivered: 5, cancelled: 0 }[phase];
  const de = lang === "de";
  const all = [
    { at: iso(1), text: de ? "Die Sendung wurde in das Zustellfahrzeug geladen." : "The parcel has been loaded onto the delivery vehicle.", location: "Hamburg" },
    { at: iso(7), text: de ? "Die Sendung wurde im Zielpaketzentrum bearbeitet." : "The parcel has been processed at the destination parcel centre.", location: "Hamburg" },
    { at: iso(19), text: de ? "Die Sendung wurde im Start-Paketzentrum bearbeitet." : "The parcel has been processed at the origin parcel centre.", location: "Köln" },
    { at: iso(22), text: de ? "Die Sendung wurde vom Absender in der Filiale eingeliefert." : "The parcel has been handed over to the carrier.", location: "Köln" },
  ];
  const events = phase === "delivered" ? [{ at: iso(0.5), text: de ? "Die Sendung wurde erfolgreich zugestellt." : "The parcel has been delivered.", location: "Hamburg" }, ...all]
    : phase === "problem" ? [{ at: iso(0.5), text: de ? "Der Empfänger wurde nicht angetroffen. Die Sendung wird in eine Filiale gebracht." : "The recipient was not at home. The parcel will be taken to a post office.", location: "Hamburg" }, ...all]
    : phase === "out_for_delivery" ? all : phase === "in_transit" ? all.slice(1) : phase === "shipped" ? all.slice(3) : [];
  const shipped = step >= 2 && phase !== "cancelled";
  return {
    orderNumber: "#1042",
    placedAt: iso(30),
    phase,
    step,
    shippedAt: shipped ? iso(22) : null,
    deliveredAt: phase === "delivered" ? iso(0.5) : null,
    eta: estimateDelivery(phase, shipped ? new Date(now - 22 * 3_600_000) : null, "DE"),
    destination: { zip: "20095", city: "Hamburg", country: "DE" },
    items: [
      { name: de ? "Gesichtsserum Hyaluron 30 ml" : "Hyaluronic face serum 30 ml", quantity: 2, image: null },
      { name: de ? "Tagescreme Reisegröße 15 ml" : "Day cream travel size 15 ml", quantity: 1, image: null },
    ],
    packages: shipped ? [{ trackingNumber: "00340434161094042557", carrier: "DHL", status: null, carrierUrl: null, deliveredAt: null, events }] : [],
    lang,
  };
}

const TP_CSS = `
.tp-root .tp-card{background:var(--tp-card);border:1px solid var(--tp-border);border-radius:var(--tp-r);box-shadow:var(--tp-shadow)}
.tp-root .tp-head{font-family:var(--tp-head);font-weight:var(--tp-head-weight)}
.tp-root .tp-btn{display:inline-flex;align-items:center;justify-content:center;gap:.5rem;min-height:46px;padding:0 1.25rem;border-radius:min(var(--tp-r),999px);font-size:14.5px;font-weight:600;transition:transform .15s ease, box-shadow .15s ease, background .15s ease;width:100%}
.tp-root .tp-btn:hover{transform:translateY(-1px)}
.tp-root .tp-btn-primary{background:var(--tp-brand);color:var(--tp-brand-fg);box-shadow:0 6px 16px -8px var(--tp-brand)}
.tp-root .tp-btn-secondary{background:var(--tp-card);border:1px solid var(--tp-border);box-shadow:var(--tp-shadow)}
.tp-root .tp-input{height:46px;padding:0 .9rem;border-radius:calc(var(--tp-r)*0.6);border:1px solid var(--tp-border);background:transparent;font-size:15px;outline:none;width:100%}
.tp-root .tp-input:focus{border-color:var(--tp-brand);box-shadow:0 0 0 3px color-mix(in srgb,var(--tp-brand) 20%,transparent)}
.tp-root .tp-scroll{scrollbar-width:none}.tp-root .tp-scroll::-webkit-scrollbar{display:none}
.tp-root .tp-faq summary::-webkit-details-marker{display:none}
.tp-root .tp-progress{animation:tp-grow 1.1s cubic-bezier(.2,.8,.2,1) both}
@keyframes tp-grow{from{width:0}}
.tp-root .tp-current{box-shadow:0 0 0 0 color-mix(in srgb,var(--tp-brand) 45%,transparent);animation:tp-pulse 2s ease-out infinite}
@keyframes tp-pulse{0%{box-shadow:0 0 0 0 color-mix(in srgb,var(--tp-brand) 45%,transparent)}100%{box-shadow:0 0 0 12px transparent}}
.tp-root .tp-ping{animation:tp-ping 1.6s cubic-bezier(0,0,.2,1) infinite}
@keyframes tp-ping{0%{transform:scale(1);opacity:1}80%,100%{transform:scale(1.9);opacity:0}}
.tp-root .tp-bounce{animation:tp-bounce 2.4s ease-in-out infinite}
@keyframes tp-bounce{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}
@media (prefers-reduced-motion: reduce){.tp-root *{animation:none!important;transition:none!important}}
`;

/** Lade- und Fehlerzustand im Seitenlayout (ohne Konfiguration) */
export function TrackingShell({ children }: { children: ReactNode }) {
  return <div className="grid min-h-screen place-items-center bg-[#f7f6f4] p-6 text-center text-[14px] text-[#5f5f66]">{children}</div>;
}

export const useTrackingLang = (initial: TrackingPageLang) => {
  const [lang, setLang] = useState<TrackingPageLang>(initial);
  useEffect(() => setLang(initial), [initial]);
  return [lang, setLang] as const;
};
